import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, clearSession } from './api';
import type { AnchorState, Partner } from '../types/anchor';

const EMPTY: AnchorState = {
  partners: [],
  capitalChannels: [],
  governmentPathways: [],
  infrastructureSlots: [],
  evidence: [],
  meetings: [],
  decisions: [],
  reviews: [],
  meta: {},
  settings: {},
  tactical: { metrics: [], day15Gates: [] },
  strategicNetwork: { layers: [], health: 0 },
  regulatedStack: { dubai: [], saudi: [] },
  ecosystem: { names: [] },
};

type StoreStatus = 'loading' | 'ready' | 'error';

type StoreValue = {
  state: AnchorState;
  status: StoreStatus;
  error: string;
  refresh: () => Promise<void>;
  createPartner: (input: Partial<Partner> & { name: string }) => Promise<Partner>;
  updatePartner: (id: string, changes: Partial<Partner>) => Promise<Partner>;
  updateMeta: (changes: Record<string, unknown>) => Promise<void>;
  importSnapshot: (snapshot: AnchorState) => Promise<void>;
  exportSnapshot: () => AnchorState;
};

const StoreContext = createContext<StoreValue | null>(null);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AnchorState>(EMPTY);
  const [status, setStatus] = useState<StoreStatus>('loading');
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const snapshot = await api.getState();
    setState({ ...EMPTY, ...snapshot });
    setStatus('ready');
    setError('');
  }, []);

  useEffect(() => {
    refresh().catch((err: unknown) => {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 401) {
        clearSession();
        window.location.reload();
        return;
      }
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to load Anchor state');
    });
  }, [refresh]);

  const createPartner = useCallback(async (input: Partial<Partner> & { name: string }) => {
    const { id: _omit, ...rest } = input;
    const record = await api.createRecord<Omit<Partner, 'id'> & { id?: string }>('partners', {
      type: 'Strategic',
      score: 0,
      evidence: 'P0',
      stage: 'Identified',
      next: 'Define a concrete ask',
      due: 'Unscheduled',
      signal: 'watch',
      region: 'Global',
      strategic: 0,
      ...rest,
    });
    const partner = record as Partner;
    setState((s) => ({ ...s, partners: [...(s.partners || []), partner] }));
    return partner;
  }, []);

  const updatePartner = useCallback(async (id: string, changes: Partial<Partner>) => {
    const record = await api.updateRecord<Partner>('partners', id, changes);
    setState((s) => ({
      ...s,
      partners: (s.partners || []).map((p) => (p.id === id ? record : p)),
    }));
    return record;
  }, []);

  const updateMeta = useCallback(async (changes: Record<string, unknown>) => {
    const nextMeta = { ...(state.meta || {}), ...changes };
    await api.putDocument('meta', nextMeta);
    setState((s) => ({ ...s, meta: nextMeta }));
  }, [state.meta]);

  const importSnapshot = useCallback(
    async (snapshot: AnchorState) => {
      await api.importState(snapshot);
      await refresh();
    },
    [refresh],
  );

  const exportSnapshot = useCallback(() => state, [state]);

  const value = useMemo<StoreValue>(
    () => ({
      state,
      status,
      error,
      refresh,
      createPartner,
      updatePartner,
      updateMeta,
      importSnapshot,
      exportSnapshot,
    }),
    [state, status, error, refresh, createPartner, updatePartner, updateMeta, importSnapshot, exportSnapshot],
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore requires StoreProvider');
  return ctx;
}

type UseThemeOptions = {
  /** Persist preference to localStorage. Default true. Public pages should pass false. */
  persist?: boolean;
};

/** Resolves 'system' | 'light' | 'dark' onto <html data-theme>, and keeps
 * following the OS while the preference is 'system'. Pass null until hydrated. */
export function useTheme(preference: string | null = 'system', options: UseThemeOptions = {}) {
  const persist = options.persist !== false;
  useEffect(() => {
    if (preference == null) return undefined;
    if (typeof document === 'undefined') return undefined;
    const mq =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;

    const apply = () => {
      const dark = preference === 'dark' || (preference === 'system' && !!mq && mq.matches);
      document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    };

    if (persist) {
      try {
        localStorage.setItem('anchor.theme', preference);
        localStorage.setItem('anchor-theme', preference);
      } catch {
        /* private mode */
      }
    }

    apply();
    if (preference !== 'system' || !mq) return undefined;
    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    mq.addListener(apply);
    return () => mq.removeListener(apply);
  }, [preference, persist]);
}
